import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import TaskRow, { isOverdue } from "./TaskRow";

const TasksPanel = ({ engagementId }) => {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("open");
  const [members, setMembers] = useState([]);
  const [adding, setAdding] = useState(null);
  const [form, setForm] = useState({
    title: "",
    assignee_id: "",
    due_date: "",
    priority: "medium",
  });

  const load = async () => {
    const r = await api.get(`/engagements/${engagementId}/tasks`);
    setTasks(r.data.items);
  };

  const loadMembers = async () => {
    const [me, others] = await Promise.all([
      api.get("/auth/me"),
      api.get("/users/firm"),
    ]);
    setMembers([me.data, ...others.data.items]);
  };

  useEffect(() => {
    load();
    loadMembers();
  }, [engagementId]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      const r = await api.post(`/engagements/${engagementId}/tasks`, {
        title: form.title,
        assignee_id: form.assignee_id || null,
        due_date: form.due_date || null,
        priority: form.priority,
        parent_id: adding === "root" ? null : adding,
      });
      // Optimistic insert so the row never blinks out during refetch
      const newTask = { ...r.data, subtasks: [] };
      if (newTask.parent_id) {
        setTasks((prev) =>
          prev.map((t) =>
            t.id === newTask.parent_id
              ? { ...t, subtasks: [...(t.subtasks || []), newTask] }
              : t,
          ),
        );
      } else {
        setTasks((prev) => [newTask, ...prev]);
      }
      setForm({ title: "", assignee_id: "", due_date: "", priority: "medium" });
      setAdding(null);
      load();
      toast.success("Task added.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add task");
    }
  };

  const filtered = (tasks || []).filter((t) => {
    if (filter === "open") return t.status !== "done";
    if (filter === "done") return t.status === "done";
    return true;
  });

  const openCount = (tasks || []).filter((t) => t.status !== "done").length;
  const overdueCount = (tasks || []).filter((t) =>
    isOverdue(t.due_date, t.status),
  ).length;

  return (
    <div className="card-flat" data-testid="tasks-panel">
      <div className="flex items-center justify-between mb-1">
        <p className="label-eyebrow">
          Work Plan · {openCount} open
          {overdueCount > 0 && (
            <span className="text-[#C94A3D]"> · {overdueCount} overdue</span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <select
            data-testid="tasks-filter"
            className="text-xs font-mono uppercase tracking-widest px-2 py-1 bg-transparent border border-[#1A2F24]/12"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="open">OPEN</option>
            <option value="done">DONE</option>
            <option value="all">ALL</option>
          </select>
          <button
            onClick={() => setAdding(adding === "root" ? null : "root")}
            className="btn-primary"
            data-testid="task-add-root-btn"
          >
            <Plus size={12} className="inline mr-2" /> Add Task
          </button>
        </div>
      </div>
      <h2 className="font-serif text-2xl text-[#112018]">Tasks & Subtasks</h2>

      {adding !== null && (
        <form
          onSubmit={create}
          className="mt-4 p-4 bg-[#F7F6F2] border border-[#1A2F24]/12 space-y-3 fade-up"
          data-testid="task-add-form"
        >
          <p className="label-eyebrow">
            {adding === "root" ? "New Task" : "New Subtask"}
          </p>
          <input
            data-testid="task-new-title"
            autoFocus
            required
            className="input-clean"
            placeholder="Task title…"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <div className="grid grid-cols-3 gap-2">
            <select
              data-testid="task-new-assignee"
              className="input-clean text-xs"
              value={form.assignee_id}
              onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}
            >
              <option value="">Unassigned</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <input
              data-testid="task-new-due"
              type="date"
              className="input-clean text-xs"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            />
            <select
              data-testid="task-new-priority"
              className="input-clean text-xs"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            >
              <option value="low">Low priority</option>
              <option value="medium">Medium priority</option>
              <option value="high">High priority</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              className="btn-primary"
              data-testid="task-new-submit"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => setAdding(null)}
              className="btn-outline"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-4 divide-y divide-[#1A2F24]/8">
        {filtered.length === 0 ? (
          <p className="text-sm text-[#606C64] italic py-8 text-center">
            {filter === "done"
              ? "No completed tasks yet."
              : "No open tasks. Add one to begin planning the work."}
          </p>
        ) : (
          filtered.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              onChange={load}
              onAddSubtask={(pid) => setAdding(pid)}
              members={members}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default TasksPanel;
